import React, { createRef, ReactElement } from 'react';
import { createRoot } from 'react-dom/client';
import { Toast, ToastMessage, ToastProps } from 'primereact/toast';
import { IconType } from 'primereact/utils';
import { ReactComponent as CloseIcon } from '../assets/svg/primereact-icons/toaster-close.svg';
import { ReactComponent as SuccessIcon } from '../assets/svg/primereact-icons/toaster-sucess.svg';
import { ReactComponent as InfoIcon } from '../assets/svg/primereact-icons/toaster-info.svg';
import { ReactComponent as ErrorIcon } from '../assets/svg/primereact-icons/toaster-error.svg';
import { ReactComponent as WarningIcon } from '../assets/svg/primereact-icons/toaster-warning.svg';
import classNames from 'classnames';
import { BrowserRouter, Link } from 'react-router-dom';
import { flushSync } from 'react-dom';

export const toastRef = createRef<Toast>();

const closeIcon: IconType<ToastProps> = () => <CloseIcon />;
const getIcon = (severity: ToastMessage['severity']) => {
  const classes = classNames(`p-icon p-toast-message-icon ${severity}`);
  switch (severity) {
    case 'success':
      return <SuccessIcon className={classes} />;
    case 'warn':
      return <WarningIcon className={classes} />;
    case 'error':
      return <ErrorIcon className={classes} />;
    default:
      return <InfoIcon className={classes} />;
  }
};

const isReactElement = (variable: ToastMessage['detail']): variable is React.ReactElement => {
  return React.isValidElement(variable);
};

const isReactRouterLink = (node: ReactElement) => {
  if (React.isValidElement(node)) {
    if (typeof node.type !== 'string') {
      const componentType = node.type;
      // Check if the componentType is Link
      if (componentType === Link) {
        return true;
      }
    }
  }
  return false;
};

const hasLinkInsideMessageDetail = (node: React.ReactNode): boolean => {
  if (typeof node === 'string') {
    return false;
  }
  if (React.isValidElement(node)) {
    if (node.type === 'a' || isReactRouterLink(node)) return true;
    const children = node.props.children;
    if (Array.isArray(children)) {
      return children.some(reactElement => hasLinkInsideMessageDetail(reactElement));
    }
    return hasLinkInsideMessageDetail(children);
  }
  return false;
};

export type SystemToast = {
  severity: ToastMessage['severity'];
  detail: ToastMessage['detail'];
  summary: ToastMessage['summary'];
  sticky?: ToastMessage['sticky'];
};

type CurrentToastsInDom = {
  details: Element[];
  summary: Element[];
  icons: Element[];
};

const getCurrentToastsInDom = (toastElement: HTMLDivElement | undefined): CurrentToastsInDom => {
  const toastDetailsElements = toastElement && toastElement.getElementsByClassName('p-toast-detail');
  const toastSummaryElements = toastElement && toastElement.getElementsByClassName('p-toast-summary');
  const toastIconsElements = toastElement && toastElement.getElementsByClassName('p-toast-message-icon');
  const details = toastDetailsElements && toastDetailsElements.length > 0 ? Array.from(toastDetailsElements) : [];
  const summary = toastSummaryElements && toastSummaryElements.length > 0 ? Array.from(toastSummaryElements) : [];
  const icons = toastIconsElements && toastIconsElements.length > 0 ? Array.from(toastIconsElements) : [];
  return { details, summary, icons };
};

const getContentFromJSX = (content: SystemToast['detail'] | SystemToast['summary']) => {
  if (isReactElement(content)) {
    const containerDetail = document.createElement('div');
    // We need to render with the router because it may have Links.
    flushSync(() => createRoot(containerDetail).render(<BrowserRouter>{content}</BrowserRouter>));
    return containerDetail.innerHTML;
  } else {
    return null;
  }
};

const isSameToastOnDOM = (currentToastsInDom: CurrentToastsInDom, message: SystemToast): boolean => {
  const {
    details: currentToastsDetails,
    summary: currentToastsSummary,
    icons: currentToastsIcons
  } = currentToastsInDom;
  const detailContent = getContentFromJSX(message.detail);
  const summaryContent = getContentFromJSX(message.summary);

  const summaryContentKey = !summaryContent ? 'textContent' : 'innerHTML';
  const detailContentKey = !detailContent ? 'textContent' : 'innerHTML';

  return currentToastsDetails.some((toast, index) => {
    const summaryToCompare = currentToastsSummary[index];
    const hasIconClass = currentToastsIcons[index].classList.contains(message.severity ?? '');
    const isSameDetail = toast[detailContentKey] === (!detailContent ? message.detail : detailContent);
    const isSameSummary = summaryToCompare[summaryContentKey] === (!summaryContent ? message.summary : summaryContent);
    return isSameDetail && isSameSummary && hasIconClass;
  });
};

const hideSameToasts = (currentToastsInDom: CurrentToastsInDom, message: SystemToast): void => {
  const detailContent = getContentFromJSX(message.detail);
  const toastsThatShouldBeHidden = currentToastsInDom.details.filter(toast =>
    detailContent ? toast.innerHTML === detailContent : toast.textContent === message.detail
  );
  toastsThatShouldBeHidden.forEach(toastContentDOM => {
    const toastDOM = toastContentDOM.parentElement?.parentElement?.parentElement;

    // We need to keep the toast in the DOM. Otherwise will break the app when the library removes it.
    if (toastDOM && !toastDOM.classList.contains('hide')) {
      toastDOM.classList.add('hide');
    }
  });
};

export const showToast = (message: SystemToast): void => {
  const { severity } = message;
  const icon: IconType<ToastProps> = () => getIcon(severity);
  // We need to avoid displaying the same toaster multiple times
  const currentToastsInDom = getCurrentToastsInDom(toastRef.current?.getElement());

  let sticky = severity === 'error' || severity === 'warn' || hasLinkInsideMessageDetail(message.detail);

  // Allow overriding of stickiness.
  if (message.sticky !== undefined) {
    sticky = message.sticky;
  }

  // Timeout is needed to avoid flushSync error: "Warning: flushSync was called from inside a lifecycle method. React cannot flush when React is already rendering. Consider moving this call to a scheduler task or micro task."
  setTimeout(() => {
    if (currentToastsInDom.details.length > 0 && isSameToastOnDOM(currentToastsInDom, message)) {
      if (sticky) {
        // If the toast is sticky and it's already visible, then return here as we will keep the one that's already there.
        return;
      }

      // Refresh the toast by hiding the existing visible one, and showing a new one.
      hideSameToasts(currentToastsInDom, message);
      toastRef.current?.show({
        ...message,
        closable: true,
        life: sticky ? undefined : 7500,
        sticky: false,
        icon
      });
      return;
    }

    toastRef.current?.show({
      ...message,
      closable: true,
      life: sticky ? undefined : 7500,
      sticky,
      closeIcon: closeIcon,
      icon
    });
  }, 0);
};
